import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { DetailSheet } from '@reflex/ui/components/layout/DetailSheet';
import { AgentStreamView } from '@reflex/ui/components/agent/AgentStreamView';
import { LoadingState } from '@reflex/ui/components/shared/LoadingState';
import { useAgent } from '@reflex/ui/client/hooks';
import { useOrgRoutes } from '@reflex/ui/client/context';

interface FlowRunAgentSheetProps {
  agentId: string | null;
  onClose: () => void;
}

/**
 * Slide-over sheet that zooms into an agent's chat view from within a
 * FlowRunPage. Closing the sheet returns the user to the flow run.
 *
 * Uses `key={agent.id}` on `AgentStreamView` (per AGENTS.md "Per-agent
 * stream scoping") so the inner WebSocket subscription is torn down and
 * rebuilt cleanly when switching between agents within the same run.
 */
export function FlowRunAgentSheet({ agentId, onClose }: FlowRunAgentSheetProps) {
  const open = agentId !== null;
  const routes = useOrgRoutes();
  const { data: agent, isLoading } = useAgent(agentId ?? undefined);

  return (
    <DetailSheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      title={agent?.name ?? 'Agent'}
      description="Agent stream and chat for the selected automation run step."
      onBack={onClose}
      backLabel="Back to automation run"
      headerExtra={
        agentId && (
          <Link
            to={routes.agent(agentId)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            Open full page
            <ExternalLink className="h-3 w-3" />
          </Link>
        )
      }
    >
      {isLoading && <LoadingState message="Loading agent..." />}
      {!isLoading && !agent && agentId && <LoadingState message="Agent not found" />}
      {agent && <AgentStreamView key={agent.id} agent={agent} />}
    </DetailSheet>
  );
}
